import { useTranslation } from 'react-i18next';
import type { AssistantAccessArea } from '@/api/assistantConfiguration/assistantConfigurationApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { cn } from '@/lib/utils';
import { ASSISTANT_ACCESS_AREAS } from './assistantConfigurationOptions';

export interface AccessAreaRowState {
  isOn: boolean;
  isLocked: boolean;
  note: string | null;
  noteActionLabel: string | null;
}

interface AccessAreaListProps {
  resolveRowState: (area: AssistantAccessArea) => AccessAreaRowState;
  onToggle: (area: AssistantAccessArea) => void;
  onNoteAction?: (area: AssistantAccessArea) => void;
}

export function AccessAreaList({ resolveRowState, onToggle, onNoteAction }: AccessAreaListProps) {
  const { t } = useTranslation('assistant');
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {ASSISTANT_ACCESS_AREAS.map(function renderArea(definition) {
        const rowState = resolveRowState(definition.area);
        const name = t('configuration.areas.options.' + definition.area + '.name');
        return (
          <div
            key={definition.area}
            className={cn(
              'flex items-start gap-3 p-4 rounded-2xl border transition-colors',
              rowState.isOn === true
                ? 'border-zooprimary bg-zoovpalelavender bg-opacity-30'
                : 'border-zooborder bg-zoowhite'
            )}
          >
            <div className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
              <Icon name={rowState.isLocked === true ? 'Lock' : definition.iconName} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-zooindigo">{name}</p>
              <p className="text-xs text-zoomuted mt-0.5 leading-snug">
                {t('configuration.areas.options.' + definition.area + '.description')}
              </p>
              {rowState.note !== null ? (
                <p className="text-xs text-zoowarningtext mt-1.5 flex flex-wrap items-center gap-x-2">
                  <span>{rowState.note}</span>
                  {rowState.noteActionLabel !== null && onNoteAction !== undefined ? (
                    <Button
                      variant="link"
                      className="text-xs"
                      onClick={function runNoteAction() {
                        onNoteAction(definition.area);
                      }}
                    >
                      {rowState.noteActionLabel}
                    </Button>
                  ) : null}
                </p>
              ) : null}
            </div>
            <ToggleSwitch
              isChecked={rowState.isOn}
              isDisabled={rowState.isLocked}
              accessibleLabel={name}
              onToggle={function toggle() {
                onToggle(definition.area);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
